"use client";

import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconCircleCheckOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { usePolymarketMarket, usePolymarketMidpoint } from "@/hooks/usePolymarketData";
import type { Edge, Node } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { POLYGON_CHAIN_ID, POLYGON_PUSD_ADDRESS } from "@/lib/polymarket-constants";
import TokenAmountInput from "./TokenAmountInput";
import VariableInput from "./VariableInput";
interface PolymarketBetConfig {
  // Mode: manual (select market/outcome) vs copy (all from variables)
  mode: "manual" | "copy";

  // Manual mode fields
  marketUrl: string;
  outcome: string;
  groupOutcome: string; // For multi-market events: the selected sub-market question
  tokenId: string; // Direct tokenId for multi-market events
  conditionId: string; // Condition ID (0x hex) for market params lookup
  side: string; // "BUY" | "SELL" or {{variable}}
  orderType: string; // "GTC" | "FOK" | "FAK" or {{variable}}

  // Copy mode fields - all from trigger variables
  tokenIdVariable: string; // e.g., "{{root.result.trade.tokenId}}"
  sideVariable: string; // e.g., "{{root.result.trade.side}}"

  // Amount configuration
  amountMode: "fixed" | "proportional";
  fixedAmount: number | string;
  dynamicAmount: string; // e.g., "{{root.result.trade.size}}"
  multiplier: number | string;
  minAmount: number | string;
  maxAmount: number | string;

  // Price configuration
  priceMode: "market" | "limit" | "copy";
  limitPrice: number | string;
  copyPriceVariable: string; // e.g., "{{root.result.trade.price}}"
}
interface PolymarketMarketInputProps {
  config: Partial<PolymarketBetConfig>;
  onChange: (config: Partial<PolymarketBetConfig>) => void;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: any[];
}
import { tokenAmountToString } from "@/lib/workflow/tokenAmount";
const DEFAULT_CONFIG: PolymarketBetConfig = {
  mode: "manual",
  marketUrl: "",
  outcome: "",
  groupOutcome: "",
  tokenId: "",
  conditionId: "",
  side: "BUY",
  orderType: "FAK",
  // FAK for market orders (immediate fill)
  // Copy mode defaults
  tokenIdVariable: "{{root.result.trade.tokenId}}",
  sideVariable: "{{root.result.trade.side}}",
  // Amount
  amountMode: "fixed",
  fixedAmount: "1000000",
  dynamicAmount: "{{root.result.trade.size}}",
  multiplier: 1.0,
  minAmount: 1,
  maxAmount: 1,
  // Price
  priceMode: "market",
  limitPrice: 0.5,
  copyPriceVariable: "{{root.result.trade.price}}"
};

/**
 * Polymarket-style betting interface with copy-trading support
 */
export default function PolymarketMarketInput({
  config: propConfig,
  onChange,
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  nodeDefinitions = []
}: PolymarketMarketInputProps) {
  // Merge config, but use defaults for falsy values on key fields
  const config: PolymarketBetConfig = {
    ...DEFAULT_CONFIG,
    ...propConfig,
    // Override falsy values with defaults
    mode: propConfig.mode || DEFAULT_CONFIG.mode,
    side: propConfig.side || DEFAULT_CONFIG.side,
    amountMode: propConfig.amountMode || DEFAULT_CONFIG.amountMode,
    priceMode: propConfig.priceMode || DEFAULT_CONFIG.priceMode,
    orderType: propConfig.orderType || DEFAULT_CONFIG.orderType,
    fixedAmount: tokenAmountToString(propConfig.fixedAmount) ?? DEFAULT_CONFIG.fixedAmount
  };
  const isCopyMode = config.mode === "copy";

  // Variable toggle state: tracks which fields are forced into variable mode
  const [forceVarFields, setForceVarFields] = useState<Record<string, boolean>>({});
  const hasVariable = (val: any): boolean => typeof val === "string" && val.includes("{{") && val.includes("}}");
  const isFieldVariable = useCallback((field: string, val: any): boolean => forceVarFields[field] || hasVariable(val), [forceVarFields]);

  // Renders a field with variable toggle support.
  // When in variable mode: shows VariableInput + "Use fixed value" button.
  // When not: shows the normal input with a hover-reveal {{ }} button.
  const varToggle = (field: string, value: any, normalEl: React.ReactNode, opts?: {
    placeholder?: string;
    defaultValue?: any;
    onChange?: (v: string) => void;
  }) => {
    if (isFieldVariable(field, value)) {
      return <div className="space-y-1">
          <VariableInput label="" value={value != null ? String(value) : ""} onChange={opts?.onChange || ((v: string) => updateConfig({
          [field]: v
        } as any))} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder={opts?.placeholder || "Type {{ for variables"} />
          <button type="button" onClick={() => {
          setForceVarFields(prev => ({
            ...prev,
            [field]: false
          }));
          if (opts?.defaultValue !== undefined) {
            updateConfig({
              [field]: opts.defaultValue
            } as any);
          }
        }} className="micro-label hover:text-ds-text-subtle transition-colors dark:hover:text-zinc-300">
            Use fixed value
          </button>
        </div>;
    }
    return <div className="space-y-1">
        {normalEl}
        <button type="button" onClick={() => setForceVarFields(prev => ({
        ...prev,
        [field]: true
      }))} className="micro-label hover:text-ds-text-subtle transition-colors dark:hover:text-zinc-300">
          Use variable
        </button>
      </div>;
  };

  // Check if the trigger is a polymarket-user-bet (required for copy trade mode)
  const triggerNode = useMemo(() => {
    return allNodes.find(node => node.type === "trigger");
  }, [allNodes]);
  const isPolymarketTrigger = useMemo(() => {
    if (!triggerNode?.data) return false;
    const triggerData = triggerNode.data as {
      type?: string;
    };
    return triggerData.type === "polymarket-user-bet";
  }, [triggerNode]);

  // Fetch market data using TanStack Query
  const {
    data: marketData,
    isLoading,
    error: marketError,
    refetch: refetchMarket
  } = usePolymarketMarket(config.marketUrl);

  // Find the selected sub-market for multi-market events
  // Match by groupOutcome first, fall back to tokenId (AI sets tokenId but not groupOutcome)
  const selectedSubMarket = useMemo(() => {
    if (!marketData?.isMultiMarket) return undefined;
    if (config.groupOutcome) {
      return marketData.markets.find(m => m.question === config.groupOutcome);
    }
    if (config.tokenId) {
      return marketData.markets.find(m => m.tokenIds.includes(config.tokenId));
    }
    return undefined;
  }, [marketData, config.groupOutcome, config.tokenId]);

  // Get the tokenId for the selected outcome
  const selectedTokenId = useMemo(() => {
    if (!marketData || !config.outcome) return undefined;
    // For multi-market, use the selected sub-market
    const source = selectedSubMarket || marketData;
    const outcomeIndex = source.outcomes.indexOf(config.outcome);
    if (outcomeIndex === -1) return undefined;
    return source.tokenIds[outcomeIndex];
  }, [marketData, selectedSubMarket, config.outcome]);

  // Fetch midpoint price with 3-second polling for real-time updates
  const {
    data: midpointPrice
  } = usePolymarketMidpoint(selectedTokenId, config.priceMode === "market");
  const updateConfig = (updates: Partial<PolymarketBetConfig>) => {
    // Only send the changed fields, not the full config
    onChange(updates);
  };

  // Initialize required defaults on mount
  useEffect(() => {
    const updates: Partial<PolymarketBetConfig> = {};
    if (!propConfig.mode) updates.mode = "manual";
    if (!propConfig.side) updates.side = "BUY";
    if (!propConfig.amountMode) updates.amountMode = "fixed";
    if (!propConfig.priceMode) updates.priceMode = "market";
    // orderType: FAK for market orders (immediate fill), GTC for limit orders (sit on book)
    if (!propConfig.orderType) {
      updates.orderType = (propConfig.priceMode || "market") === "market" ? "FAK" : "GTC";
    }
    if (propConfig.fixedAmount === undefined) updates.fixedAmount = "1000000";
    // Migrate legacy number values to strings via tokenAmountToString.
    else if (typeof propConfig.fixedAmount === "number") {
      updates.fixedAmount = tokenAmountToString(propConfig.fixedAmount)!;
    }
    // Copy mode: hardcode variable references so the workflow engine can resolve them
    if (propConfig.mode === "copy") {
      if (!propConfig.tokenIdVariable) updates.tokenIdVariable = "{{root.result.trade.tokenId}}";
      if (!propConfig.sideVariable) updates.sideVariable = "{{root.result.trade.side}}";
      if (!propConfig.dynamicAmount) updates.dynamicAmount = "{{root.result.trade.size}}";
      if (!propConfig.amountMode) updates.amountMode = "proportional";
    }
    if (Object.keys(updates).length > 0) {
      onChange(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select first outcome when market data loads (single-binary markets only)
  useEffect(() => {
    if (marketData && marketData.marketType === "single-binary" && !config.outcome && marketData.outcomes.length > 0) {
      updateConfig({
        outcome: marketData.outcomes[0],
        tokenId: marketData.tokenIds[0]
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketData, config.outcome]);

  // Backfill groupOutcome from tokenId when market data loads (AI sets tokenId but not groupOutcome)
  useEffect(() => {
    if (marketData?.isMultiMarket && config.tokenId && !config.groupOutcome) {
      const match = marketData.markets.find(m => m.tokenIds.includes(config.tokenId));
      if (match) {
        updateConfig({
          groupOutcome: match.question
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketData, config.tokenId, config.groupOutcome]);

  // Auto-scroll to selected sub-market (e.g. after backfill from tokenId)
  const selectedSubMarketRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedSubMarketRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth"
    });
  }, [config.groupOutcome]);
  const error = marketError?.message || null;
  const formatPrice = (price: number) => {
    const cents = Math.round(price * 100);
    return `${cents}¢`;
  };
  return <div className="min-w-0 space-y-4">
      {/* Mode Toggle: Manual vs Copy Trade */}
      <div className="space-y-2">
        <label className="field-label block">Mode</label>
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex rounded-lg p-1">
          <button type="button" onClick={() => updateConfig({
          mode: "manual",
          // Clear copy mode fields
          tokenIdVariable: "",
          sideVariable: "",
          dynamicAmount: ""
        })} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${!isCopyMode ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
            Manual
          </button>
          <button type="button" disabled={!isPolymarketTrigger} onClick={() => {
          if (!isPolymarketTrigger) return;
          updateConfig({
            mode: "copy",
            // Set hardcoded variable paths for copy mode
            tokenIdVariable: "{{root.result.trade.tokenId}}",
            sideVariable: "{{root.result.trade.side}}",
            dynamicAmount: "{{root.result.trade.size}}"
          });
        }} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${!isPolymarketTrigger ? "text-ds-text-disabled dark:text-ds-text-muted cursor-not-allowed" : isCopyMode ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
            Copy Trade
          </button>
        </div>
        <p className="label-xs">
          {isCopyMode ? "Copy trades from trigger - token, side, size, and price from trigger data" : !isPolymarketTrigger ? "Copy Trade requires a Polymarket User Bet trigger" : "Manually select market, outcome, and configure trade"}
        </p>
      </div>
      {/* COPY MODE: Simplified copy trading config */}
      {isCopyMode ? <div className="space-y-4">
          {/* Execution Settings - Auto configured */}
          <div className="space-y-2 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
            <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Execution</p>
            <div className="grid gap-1 text-xs">
              <div className="flex justify-between">
                <span className="text-blue-600/70 dark:text-blue-400/70">Price:</span>
                <span className="font-medium text-blue-700 dark:text-blue-300">Current market (midpoint)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-600/70 dark:text-blue-400/70">Order Type:</span>
                <span className="font-medium text-blue-700 dark:text-blue-300">FAK (immediate fill)</span>
              </div>
            </div>
          </div>

          {/* Position Sizing Controls - Editable */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="field-label block">Position Sizing</label>
              <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex rounded-lg p-0.5">
                <button type="button" onClick={() => updateConfig({
              amountMode: "fixed"
            })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.amountMode === "fixed" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                  Fixed
                </button>
                <button type="button" onClick={() => updateConfig({
              amountMode: "proportional"
            })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.amountMode === "proportional" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                  Proportional
                </button>
              </div>
            </div>

            {config.amountMode !== "proportional" ? varToggle("fixedAmount", config.fixedAmount, <TokenAmountInput label="" value={String(config.fixedAmount || "0")} onChange={val => {
          updateConfig({
            fixedAmount: val || "0"
          });
        }} chainId={POLYGON_CHAIN_ID} tokenAddress={POLYGON_PUSD_ADDRESS} />, {
          placeholder: "{{amount variable}}",
          defaultValue: "1000000"
        }) : <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="label-xs-caps">Multiplier</label>
                    {varToggle("multiplier", config.multiplier, <Input type="number" value={Number(config.multiplier) || 1} onChange={e => updateConfig({
                multiplier: parseFloat(e.target.value) || 1
              })} step="0.1" min="0.01" className="dark:bg-ds-neutral-700 dark:border-zinc-600 dark:text-white" />, {
                placeholder: "{{multiplier}}",
                defaultValue: 1
              })}
                  </div>
                  <div className="space-y-1">
                    <label className="label-xs-caps">Min $</label>
                    {varToggle("minAmount", config.minAmount, <Input type="number" value={Number(config.minAmount) || 1} onChange={e => updateConfig({
                minAmount: parseFloat(e.target.value) || 1
              })} min="1" className="dark:bg-ds-neutral-700 dark:border-zinc-600 dark:text-white" />, {
                placeholder: "{{min amount}}",
                defaultValue: 1
              })}
                  </div>
                  <div className="space-y-1">
                    <label className="label-xs-caps">Max $</label>
                    {varToggle("maxAmount", config.maxAmount, <Input type="number" value={Number(config.maxAmount) || 1} onChange={e => updateConfig({
                maxAmount: parseFloat(e.target.value) || 1
              })} min="1" className="dark:bg-ds-neutral-700 dark:border-zinc-600 dark:text-white" />, {
                placeholder: "{{max amount}}",
                defaultValue: 1
              })}
                  </div>
                </div>
                {typeof config.multiplier === "number" && typeof config.minAmount === "number" && typeof config.maxAmount === "number" && <p className="label-xs-caps">
                      Final amount = size × {config.multiplier}, clamped to ${config.minAmount} - ${config.maxAmount}
                    </p>}
              </>}
          </div>
        </div> : <>
          {/* MANUAL MODE: Market URL Input */}
          <div className="space-y-2">
            <label className="field-label block">Market URL</label>
            <div className="flex min-w-0 gap-2">
              <div className="min-w-0 flex-1">
                <VariableInput label="" value={config.marketUrl} onChange={v => updateConfig({
              marketUrl: v
            })} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder="https://polymarket.com/event/... or {{variable}}" />
              </div>
              {!hasVariable(config.marketUrl) && <button type="button" onClick={() => refetchMarket()} disabled={isLoading || !config.marketUrl.trim()} className="border-ds-neutral-300 text-ds-text-secondary bg-ds-surface-card hover:bg-ds-surface-hover flex h-[38px] shrink-0 items-center gap-2 self-start rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 dark:border-zinc-600">
                  <IconMagnifyingGlass className="h-4 w-4" />
                  {isLoading ? "..." : "Fetch"}
                </button>}
            </div>
            {error && <p className="flex items-center gap-1 text-sm text-red-500">
                <IconAlertInfoOutline18 className="h-4 w-4" /> {error}
              </p>}
            {marketData && <p className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <IconCircleCheckOutline18 className="h-4 w-4" /> {marketData.title}
                <span className="bg-ds-neutral-200 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled text-3xs ml-1 rounded px-1.5 py-0.5 font-medium">
                  {marketData.marketType === "neg-risk" ? "Multi-outcome" : marketData.marketType === "grouped-binary" ? "Grouped markets" : "Binary"}
                </span>
              </p>}
          </div>

          {(marketData || config.tokenId) && <div className="border-ds-border-default min-w-0 overflow-hidden rounded-xl border bg-white dark:bg-[#1a1f2e]">
              {/* Buy/Sell tabs */}
              {isFieldVariable("side", config.side) ? <div className="border-ds-border-default border-b p-3">
                  <label className="field-label-muted mb-1 block">Side</label>
                  <VariableInput label="" value={config.side} onChange={v => updateConfig({
            side: v
          })} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder="BUY, SELL, or {{variable}}" />
                  <button type="button" onClick={() => {
            setForceVarFields(prev => ({
              ...prev,
              side: false
            }));
            updateConfig({
              side: "BUY"
            });
          }} className="micro-label hover:text-ds-text-subtle mt-1 transition-colors dark:hover:text-zinc-300">
                    Use selector
                  </button>
                </div> : <div>
                  <div className="border-ds-border-default flex border-b">
                    <button type="button" onClick={() => updateConfig({
              side: "BUY"
            })} className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${config.side === "BUY" ? "border-ds-neutral-900 text-ds-text-primary border-b-2 dark:border-white" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                      Buy
                    </button>
                    <button type="button" onClick={() => updateConfig({
              side: "SELL"
            })} className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${config.side === "SELL" ? "border-ds-neutral-900 text-ds-text-primary border-b-2 dark:border-white" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                      Sell
                    </button>
                  </div>
                  <div className="px-4 pt-1">
                    <button type="button" onClick={() => setForceVarFields(prev => ({
              ...prev,
              side: true
            }))} className="micro-label hover:text-ds-text-subtle transition-colors dark:hover:text-zinc-300">
                      Use variable
                    </button>
                  </div>
                </div>}

              <div className="space-y-4 p-4">
                {marketData && (marketData.marketType === "neg-risk" || marketData.marketType === "grouped-binary") ? <div className="space-y-2">
                    <label className="field-label-muted">
                      {marketData.marketType === "neg-risk" ? "Select Outcome" : "Select Market"}
                    </label>
                    <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                      {marketData.markets.map(subMarket => {
                const yesPrice = parseFloat(subMarket.outcomePrices[0]);
                const isSelected = config.groupOutcome === subMarket.question || !config.groupOutcome && !!config.tokenId && subMarket.tokenIds.includes(config.tokenId);
                const displayLabel = subMarket.groupItemTitle || subMarket.question;
                return <button key={subMarket.id} ref={isSelected ? selectedSubMarketRef : null} type="button" onClick={() => {
                  updateConfig({
                    groupOutcome: subMarket.question,
                    outcome: "",
                    tokenId: "",
                    conditionId: ""
                  });
                }} className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-all ${isSelected ? "bg-blue-600 text-white ring-2 ring-blue-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                            <span className="mr-2 flex-1 truncate">{displayLabel}</span>
                            <span className={`shrink-0 font-semibold ${isSelected ? "text-white" : "text-green-600 dark:text-green-400"}`}>
                              {formatPrice(yesPrice)}
                            </span>
                          </button>;
              })}
                    </div>
                    {/* Outcome buttons for selected sub-market */}
                    {selectedSubMarket && <div className="mt-2 space-y-1">
                        <label className="field-label-muted">Outcome</label>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedSubMarket.outcomes.map((outcomeName, idx) => {
                  const price = parseFloat(selectedSubMarket.outcomePrices[idx]);
                  const isOutcomeSelected = config.outcome === outcomeName;
                  return <button key={outcomeName} type="button" onClick={() => {
                    updateConfig({
                      outcome: outcomeName,
                      tokenId: selectedSubMarket.tokenIds[idx],
                      conditionId: selectedSubMarket.conditionId
                    });
                  }} className={`rounded-lg px-4 py-2.5 text-center font-semibold transition-all ${isOutcomeSelected ? idx === 0 ? "bg-green-600 text-white ring-2 ring-green-400" : "bg-red-500 text-white ring-2 ring-red-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                                {outcomeName} <span className="ml-1">{formatPrice(price)}</span>
                              </button>;
                })}
                        </div>
                      </div>}
                  </div> : marketData ? <div className="grid grid-cols-2 gap-3">
                    {marketData.outcomes.map((outcomeName, idx) => {
              const staticPrice = parseFloat(marketData.outcomePrices[idx]);
              const isSelected = config.outcome === outcomeName;
              const isFirst = idx === 0;
              // Use live midpoint price for selected outcome, static price for others
              const displayPrice = isSelected && midpointPrice ? midpointPrice : staticPrice;
              return <button key={outcomeName} type="button" onClick={() => updateConfig({
                outcome: outcomeName,
                tokenId: marketData.tokenIds[idx],
                conditionId: marketData.markets[0]?.conditionId || ""
              })} className={`rounded-lg px-4 py-3 text-center font-semibold transition-all ${isSelected ? isFirst ? "bg-green-600 text-white ring-2 ring-green-400" : "bg-red-500 text-white ring-2 ring-red-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                          {outcomeName}{" "}
                          <span className={`ml-1 ${isSelected && midpointPrice ? "animate-pulse" : ""}`}>
                            {formatPrice(displayPrice)}
                          </span>
                        </button>;
            })}
                  </div> : <div className="space-y-2">
                    {config.outcome && <div className="flex items-center justify-between">
                        <span className="field-label-muted">Outcome</span>
                        <span className="text-ds-text-primary text-sm font-medium">{config.outcome}</span>
                      </div>}
                    {config.groupOutcome && <div className="flex items-center justify-between">
                        <span className="field-label-muted">Market</span>
                        <span className="text-ds-text-primary truncate text-sm">{config.groupOutcome}</span>
                      </div>}
                    {isLoading && <p className="text-ds-text-subtle text-xs">Fetching market data...</p>}
                  </div>}

                {/* Amount Mode Toggle */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="field-label-muted">Amount</label>
                    <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex rounded-lg p-0.5">
                      <button type="button" onClick={() => updateConfig({
                  amountMode: "fixed"
                })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.amountMode === "fixed" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                        Fixed
                      </button>
                      <button type="button" onClick={() => updateConfig({
                  amountMode: "proportional"
                })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.amountMode === "proportional" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                        Proportional
                      </button>
                    </div>
                  </div>

                  {config.amountMode !== "proportional" ? (/* Fixed Amount Input */
            varToggle("fixedAmount", config.fixedAmount, <TokenAmountInput label="" value={String(config.fixedAmount || "0")} onChange={val => {
              updateConfig({
                fixedAmount: val || "0"
              });
            }} chainId={137} tokenAddress="0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB" />, {
              placeholder: "{{amount variable}}",
              defaultValue: "1000000"
            })) : <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800/50 space-y-3 rounded-lg p-3">
                      {/* Variable Input */}
                      <div className="space-y-1">
                        <label className="label-xs">Source Variable</label>
                        <VariableInput label="" value={config.dynamicAmount} onChange={v => updateConfig({
                  dynamicAmount: v
                })} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder="{{root.result.body.payment.amount}}" />
                        <p className="caption-xs">
                          Tip: Click inside the variable to add nested paths like .payment.amount
                        </p>
                      </div>

                      {/* Multiplier */}
                      <div className="space-y-1">
                        <label className="label-xs">Multiplier</label>
                        {varToggle("multiplier", config.multiplier, <div className="flex items-center gap-2">
                            <Input type="number" value={Number(config.multiplier) || 1} onChange={e => updateConfig({
                    multiplier: parseFloat(e.target.value) || 1
                  })} step="0.1" min="0.1" className="text-ds-text-primary dark:bg-ds-neutral-800 w-20 text-center dark:border-zinc-600" />
                            <span className="text-ds-text-muted">×</span>
                            <div className="flex gap-1">
                              {[0.5, 1, 1.5, 2].map(m => <button key={m} type="button" onClick={() => updateConfig({
                      multiplier: m
                    })} className={`rounded px-2 py-1 text-xs font-medium ${config.multiplier === m ? "bg-blue-600 text-white" : "bg-ds-neutral-200 text-ds-text-secondary hover:bg-ds-neutral-300 dark:bg-ds-neutral-700 dark:hover:bg-zinc-600"}`}>
                                  {m}x
                                </button>)}
                            </div>
                          </div>, {
                  placeholder: "{{multiplier}}",
                  defaultValue: 1
                })}
                      </div>

                      {/* Min/Max Bounds */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="label-xs">Min Amount</label>
                          {varToggle("minAmount", config.minAmount, <div className="flex items-center gap-1">
                              <span className="text-ds-text-muted">$</span>
                              <Input type="number" value={Number(config.minAmount) || 1} onChange={e => updateConfig({
                      minAmount: parseFloat(e.target.value) || 0
                    })} className="text-ds-text-primary dark:bg-ds-neutral-800 px-2 py-1.5 dark:border-zinc-600" />
                            </div>, {
                    placeholder: "{{min amount}}",
                    defaultValue: 1
                  })}
                        </div>
                        <div className="space-y-1">
                          <label className="label-xs">Max Amount</label>
                          {varToggle("maxAmount", config.maxAmount, <div className="flex items-center gap-1">
                              <span className="text-ds-text-muted">$</span>
                              <Input type="number" value={Number(config.maxAmount) || 1} onChange={e => updateConfig({
                      maxAmount: parseFloat(e.target.value) || 1
                    })} className="text-ds-text-primary dark:bg-ds-neutral-800 px-2 py-1.5 dark:border-zinc-600" />
                            </div>, {
                    placeholder: "{{max amount}}",
                    defaultValue: 1
                  })}
                        </div>
                      </div>

                      {typeof config.multiplier === "number" && typeof config.minAmount === "number" && typeof config.maxAmount === "number" && <p className="label-xs">
                            Final amount = {config.dynamicAmount || "variable"} × {config.multiplier}, clamped to $
                            {config.minAmount}-${config.maxAmount}
                          </p>}
                    </div>}
                </div>

                {/* Price Mode */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="field-label-muted">Price</label>
                    <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex rounded-lg p-0.5">
                      <button type="button" onClick={() => updateConfig({
                  priceMode: "market",
                  orderType: "FAK"
                })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.priceMode === "market" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                        Market
                      </button>
                      <button type="button" onClick={() => updateConfig({
                  priceMode: "limit",
                  orderType: "GTC"
                })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.priceMode === "limit" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                        Limit
                      </button>
                      <button type="button" onClick={() => updateConfig({
                  priceMode: "copy",
                  orderType: "GTC"
                })} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${config.priceMode === "copy" ? "text-ds-text-primary shadow-ds-soft bg-white dark:bg-zinc-600" : "text-ds-text-muted hover:text-ds-text-secondary dark:hover:text-zinc-200"}`}>
                        Copy
                      </button>
                    </div>
                  </div>

                  {config.priceMode === "limit" && varToggle("limitPrice", config.limitPrice, <div className="flex items-center gap-3">
                        <button type="button" onClick={() => updateConfig({
                limitPrice: Math.max(0.01, (Number(config.limitPrice) || 0.5) - 0.01)
              })} className="bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded-lg px-3 py-2 text-lg dark:hover:bg-zinc-600">
                          −
                        </button>
                        <div className="flex-1 text-center">
                          <span className="text-ds-text-primary text-2xl font-semibold">
                            {formatPrice(Number(config.limitPrice) || 0.5)}
                          </span>
                        </div>
                        <button type="button" onClick={() => updateConfig({
                limitPrice: Math.min(0.99, (Number(config.limitPrice) || 0.5) + 0.01)
              })} className="bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded-lg px-3 py-2 text-lg dark:hover:bg-zinc-600">
                          +
                        </button>
                      </div>, {
              placeholder: "{{price variable}} (0.01-0.99)",
              defaultValue: 0.5
            })}

                  {config.priceMode === "copy" && <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800/50 rounded-lg p-2">
                      <VariableInput label="" value={config.copyPriceVariable} onChange={v => updateConfig({
                copyPriceVariable: v
              })} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder="{{root.result.trade.price}}" />
                    </div>}

                  {/* Order type hint - automatically set based on price mode */}
                  <p className="label-xs">
                    {config.priceMode === "market" ? "Market order (fills immediately at best price)" : "Limit order (stays on book until filled)"}
                  </p>
                </div>
              </div>
            </div>}

          {!marketData && !config.tokenId && config.marketUrl && !isLoading && !error && <div className="border-ds-border-default space-y-3 rounded-lg border p-3">
              <VariableInput label="Outcome" description='Enter outcome name (e.g., "Yes", "No", "Up", "Down") or {{variable}}' value={config.outcome} onChange={v => updateConfig({
          outcome: v
        })} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder='"Yes", "No", or {{variable}}' />
            </div>}
        </>}
    </div>;
}